// Strength of schedule: how good the teams a team has played were, and how
// good the ones it still has to play are (SCHEDULE.md, B-386).
//
// THE OPPONENTS' RECORDS, summed over each meeting — a team met twice counts
// twice — the way a standings page reads strength of schedule, and the
// opponents' average score a week, since in fantasy a team faces the points
// its opponent puts up and nothing else. The records are the ones every
// screen shows now, not as they stood the week the game was played.
//
// RANKED BY THE RECORDS, then by the points: 1 is the hardest schedule still
// to play. Before anybody has finished a game nothing tells two schedules
// apart, and nobody is ranked.
//
// ON THE WIRE, NOT A CLIENT'S ARITHMETIC (CLAUDE.md, the backend is a
// product): `ScheduleView.strength` is this, worked out by the store.

/** The fields of a week's games this reads, so the views and a store's rows both fit. */
export interface StrengthGame {
  kind: string;
  status: 'pre' | 'live' | 'final';
  home: StrengthSide;
  away: StrengthSide;
}
interface StrengthSide {
  team: { id: string; record: { wins: number; losses: number; ties: number } };
  score: number;
}

export interface StrengthRecord {
  wins: number;
  losses: number;
  ties: number;
}

export interface StrengthPart {
  /** Games in this part: finished for `played`, not yet finished for `remaining`. */
  games: number;
  /** The opponents' records now, summed over each meeting. */
  opponents: StrengthRecord;
  /** The opponents' average score a week, over the games each has finished; null before any has. */
  pointsPerGame: number | null;
}

export interface TeamStrength {
  teamId: string;
  played: StrengthPart;
  remaining: StrengthPart;
  /**
   * Where the schedule still to play stands in the league, 1 the hardest;
   * teams level share a place. Null with nothing left to play, and for
   * everybody before any game has been finished.
   */
  remainingRank: number | null;
}

/** A record's share of wins, a tie counting half; null for a record with no games. */
export function winShare(r: StrengthRecord): number | null {
  const games = r.wins + r.losses + r.ties;
  return games === 0 ? null : (r.wins + r.ties / 2) / games;
}

/**
 * Every team's strength of schedule over the regular season: the weeks of
 * kind `regular` and their games of kind `regular`. A playoff game's
 * opponent is not known until the bracket is drawn, and a median game has no
 * opponent.
 */
export function scheduleStrength(
  periods: readonly { kind: string; matchups: readonly StrengthGame[] }[],
): TeamStrength[] {
  const games = periods
    .filter((p) => p.kind === 'regular')
    .flatMap((p) => p.matchups.filter((m) => m.kind === 'regular'));

  // Each team's average score a week, over the games it has finished.
  const scored = new Map<string, { points: number; games: number }>();
  const records = new Map<string, StrengthRecord>();
  const order: string[] = [];
  for (const m of games) {
    for (const s of [m.home, m.away]) {
      if (!records.has(s.team.id)) {
        records.set(s.team.id, s.team.record);
        order.push(s.team.id);
      }
      if (m.status !== 'final') continue;
      const t = scored.get(s.team.id) ?? { points: 0, games: 0 };
      scored.set(s.team.id, { points: t.points + s.score, games: t.games + 1 });
    }
  }
  const average = (teamId: string): number | null => {
    const t = scored.get(teamId);
    return t === undefined || t.games === 0 ? null : t.points / t.games;
  };

  const parts = new Map<string, { played: string[]; remaining: string[] }>();
  for (const id of order) parts.set(id, { played: [], remaining: [] });
  for (const m of games) {
    const into = m.status === 'final' ? 'played' : 'remaining';
    parts.get(m.home.team.id)![into].push(m.away.team.id);
    parts.get(m.away.team.id)![into].push(m.home.team.id);
  }
  const partOf = (opponents: readonly string[]): StrengthPart => {
    const sum: StrengthRecord = { wins: 0, losses: 0, ties: 0 };
    const averages: number[] = [];
    for (const id of opponents) {
      const r = records.get(id)!;
      sum.wins += r.wins;
      sum.losses += r.losses;
      sum.ties += r.ties;
      const a = average(id);
      if (a !== null) averages.push(a);
    }
    return {
      games: opponents.length,
      opponents: sum,
      pointsPerGame:
        averages.length === 0 ? null : round(averages.reduce((x, y) => x + y, 0) / averages.length),
    };
  };
  const out = order.map((teamId) => {
    const p = parts.get(teamId)!;
    return {
      teamId,
      played: partOf(p.played),
      remaining: partOf(p.remaining),
      remainingRank: null as number | null,
    };
  });

  // THE RANK, once a game has been finished: by the opponents' share of
  // wins, then their points a week; a part with neither is the easiest.
  if (scored.size === 0) return out;
  const key = (t: TeamStrength) =>
    [winShare(t.remaining.opponents) ?? -1, t.remaining.pointsPerGame ?? -1] as const;
  const ranked = out
    .filter((t) => t.remaining.games > 0)
    .sort((a, b) => key(b)[0] - key(a)[0] || key(b)[1] - key(a)[1]);
  ranked.forEach((t, i) => {
    const first = ranked.findIndex((x) => key(x)[0] === key(t)[0] && key(x)[1] === key(t)[1]);
    t.remainingRank = (first === -1 ? i : first) + 1;
  });
  return out;
}

/** To the hundredth: an average of averages carries no more than that. */
function round(n: number): number {
  return Math.round(n * 100) / 100;
}
